import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { keepPreviousData } from '@tanstack/react-query';
import type {
  CashListTransactionType,
  GetCashListTransactionsParams,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  useCashListTransactionsPhoenix,
  useGetNursingHomeEmployees,
  useGetPaymentProcessor,
} from '@pflegenavi/frontend/api-nursing-home';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import {
  useFormatDate,
  useFormatTime,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import { generatePath, nhAppMainPages } from '@pflegenavi/frontend/routing';
import { FeatureFlag } from '@pflegenavi/shared/api';
import type { FilterDefinition, UrlSort } from '@pflegenavi/web-components';
import {
  makeConfig,
  PaginatedTable2,
  useUrlTableState,
} from '@pflegenavi/web-components';
import type { CashTransactionTableRowModel } from '../../interfaces/CashTransactionTableRowModel';
import { formatCashTransactionTableRowModel } from '../../helpers/formatCashTransactionTableRowModel';
import type {
  CashListTransactionFilterKey,
  CashListTransactionFiltersState,
  CashListTransactionTab,
} from '../../useCashListTransactionFilters';
import { CoinsModal } from '../modals/CoinsModal';
import { InfoColumn } from '../groupTable/Columns';
import {
  AmountColumn,
  BalanceColumn,
  DateColumn,
  EmployeeColumn,
  FeesColumn,
  ResidentColumn,
  TypeColumn,
} from './Columns';

type ColumnNames =
  | 'type'
  | 'resident'
  | 'date'
  | 'employee'
  | 'balance'
  | 'amount'
  | 'fees'
  | 'info';

type ListParams = GetCashListTransactionsParams['params'];
type SortColumn = NonNullable<ListParams['orderBy']>;

const SORT_COLUMNS: readonly SortColumn[] = ['creation_datetime', 'amount'];
const DEFAULT_SORT: UrlSort<SortColumn> = {
  column: 'creation_datetime',
  direction: 'desc',
};
const DEFAULT_PAGE_SIZE = 10;

const useTabCount = (
  scope: ListParams,
  type: CashListTransactionType | undefined,
  enabled = true
) => {
  const { data } = useCashListTransactionsPhoenix(
    { ...scope, filters: { ...scope.filters, type } },
    { enabled, placeholderData: keepPreviousData }
  );
  return data?.meta.total_count ?? 0;
};

function useCashListTransactionFilterDefinitions(
  filtersState: CashListTransactionFiltersState
): Array<FilterDefinition<CashListTransactionFilterKey>> {
  const { t } = useTranslation();
  const { values, set, cashListId, cashLists } = filtersState;
  const { selectedNursingHome } = useNursingHomeContext();

  const { data: employeesData } = useGetNursingHomeEmployees({
    nursingHomeId: selectedNursingHome?.id,
    paginationParams: { page_size: 500 },
  });

  const cashListOptions = useMemo(
    () =>
      cashLists.map((cashList) => ({ id: cashList.id, label: cashList.name })),
    [cashLists]
  );

  const employeeOptions = useMemo(
    () =>
      (employeesData?.data ?? []).map((employee) => ({
        id: employee.id,
        label: `${employee.first_name} ${employee.last_name}`,
      })),
    [employeesData]
  );

  return useMemo(
    () => [
      {
        key: 'cashList',
        type: 'select',
        label: t('cashManagement.filters.cash-list'),
        highlighted: true,
        // Always resolves to a cash list, so there is nothing to clear.
        clearable: false,
        options: cashListOptions,
        value: cashListId,
        onChange: (value) => set('cashList', value),
      },
      {
        key: 'createdAt',
        type: 'dateRange',
        label: t('cashManagement.table-head.datetime'),
        range: values.createdAt,
        onChange: (range) => set('createdAt', range),
      },
      {
        key: 'employee',
        type: 'select',
        label: t('common.employee'),
        options: employeeOptions,
        value: values.employee,
        onChange: (value) => set('employee', value),
      },
    ],
    [
      t,
      cashListOptions,
      cashListId,
      values.createdAt,
      values.employee,
      employeeOptions,
      set,
    ]
  );
}

interface CashListTransactionTableProps {
  cashListId: string;
  filtersState: CashListTransactionFiltersState;
}

export const CashListTransactionTable: FC<CashListTransactionTableProps> = ({
  cashListId,
  filtersState,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const fTime = useFormatTime();
  const theme = useTheme();
  const navigate = useNavigate();
  const paymentProcessor = useGetPaymentProcessor();

  const stripeTransferEnabled = useFeatureFlagEnabled(
    FeatureFlag.AccountingPayments
  );
  const hasAccountingPaymentsNew = useFeatureFlagEnabled(
    FeatureFlag.AccountingPaymentsNew
  );
  const groupDetailPath = hasAccountingPaymentsNew
    ? nhAppMainPages.NURSING_HOME_ACCOUNTING_GROUP_DETAIL
    : nhAppMainPages.CASH_MANAGEMENT_GROUP_DETAIL;

  const { page, pageSize, sort, setPage, setPageSize, setSort } =
    useUrlTableState({
      defaultPageSize: DEFAULT_PAGE_SIZE,
      sortColumns: SORT_COLUMNS,
      defaultSort: DEFAULT_SORT,
    });
  const activeSort = sort ?? DEFAULT_SORT;

  const { filters, tab, cashLists, set } = filtersState;

  const scope = useMemo<ListParams>(
    () => ({
      cashListId,
      page: 1,
      pageSize: 1,
      filters: { ...filters, type: undefined },
    }),
    [cashListId, filters]
  );

  const allCount = useTabCount(scope, undefined);
  const depositCount = useTabCount(scope, 'deposit');
  const withdrawalCount = useTabCount(scope, 'withdrawal');
  const adjustmentCount = useTabCount(scope, 'adjustment');
  const stripeTransferCount = useTabCount(
    scope,
    'stripe_transfer',
    stripeTransferEnabled
  );

  const tabItems = useMemo(
    () => [
      {
        value: 'all' as const,
        label: t('common.all'),
        color: 'default' as const,
        count: allCount,
      },
      {
        value: 'deposit' as const,
        label: t('cashManagement.table-head.deposit'),
        color: 'success' as const,
        count: depositCount,
      },
      {
        value: 'withdrawal' as const,
        label: t('cashManagement.table-head.withdrawal'),
        color: 'error' as const,
        count: withdrawalCount,
      },
      {
        value: 'adjustment' as const,
        label: t('cashManagement.table-head.adjustments'),
        color: 'info' as const,
        count: adjustmentCount,
      },
      ...(stripeTransferEnabled
        ? [
            {
              value: 'stripe_transfer' as const,
              label: t('cashManagement.table-head.stripe-transfer'),
              color: 'default' as const,
              count: stripeTransferCount,
            },
          ]
        : []),
    ],
    [
      t,
      allCount,
      depositCount,
      withdrawalCount,
      adjustmentCount,
      stripeTransferCount,
      stripeTransferEnabled,
    ]
  );

  const { data, isLoading, isError } = useCashListTransactionsPhoenix(
    {
      cashListId,
      page: page + 1, // Flop is 1-indexed, MUI TablePagination is 0-indexed
      pageSize,
      orderBy: activeSort.column,
      orderDirection: activeSort.direction,
      filters,
    },
    { placeholderData: keepPreviousData }
  );

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((transaction) =>
        formatCashTransactionTableRowModel(transaction, {
          fCurrencyCents,
          fDate,
          fTime,
          theme,
          t,
        })
      ),
    [data, fCurrencyCents, fDate, fTime, theme, t]
  );

  const [detailsRow, setDetailsRow] = useState<
    CashTransactionTableRowModel | undefined
  >(undefined);

  const selectedCashListName = cashLists.find(
    (cashList) => cashList.id === cashListId
  )?.name;

  const filterItems = useCashListTransactionFilterDefinitions(filtersState);

  const config = useMemo(() => {
    const sortBy = (column: SortColumn) => (direction: 'ASC' | 'DESC') =>
      setSort({ column, direction: direction === 'ASC' ? 'asc' : 'desc' });

    return makeConfig<
      CashTransactionTableRowModel,
      ColumnNames,
      CashListTransactionFilterKey
    >({
      showAll: undefined,
      onClickRow: (row) => {
        if (row.groupId) {
          navigate(generatePath(groupDetailPath, { groupId: row.groupId }));
        } else {
          setDetailsRow(row);
        }
      },
      options: {
        items: ({ groupId }) => [
          {
            key: 'view-details',
            label: t('cashManagement.view-transaction-details'),
            onClick: (row) => setDetailsRow(row),
          },
          ...(groupId
            ? [
                {
                  key: 'view-group',
                  label: t('cashManagement.view-transaction-group'),
                  onClick: () =>
                    navigate(generatePath(groupDetailPath, { groupId })),
                },
              ]
            : []),
        ],
      },
      tabs: {
        selectedItem: tab,
        updateFilterModel: (value: CashListTransactionTab) =>
          set('tab', value === 'all' ? undefined : value),
        items: tabItems,
      },
      filters: { variant: 'pills', items: filterItems },
      columns: [
        {
          key: 'type',
          label: t('common.type'),
          component: TypeColumn,
        },
        {
          key: 'resident',
          label: t('common.resident'),
          component: ResidentColumn,
          maxWidth: 180,
        },
        {
          key: 'date',
          label: t('cashManagement.table-head.datetime'),
          component: DateColumn,
          onSort: sortBy('creation_datetime'),
        },
        {
          key: 'employee',
          label: t('common.employee'),
          component: EmployeeColumn,
        },
        {
          key: 'balance',
          label: hasAccountingPaymentsNew
            ? t('cashManagement.table-head.balance-new-wording')
            : t('cashManagement.table-head.balance'),
          component: BalanceColumn,
          align: 'right',
        },
        {
          key: 'amount',
          label: t('common.amount'),
          component: AmountColumn,
          align: 'right',
          onSort: sortBy('amount'),
        },
        ...(paymentProcessor === 'mangopay'
          ? [
              {
                key: 'fees',
                label: t('common.fees'),
                component: FeesColumn,
                align: 'right',
              } as const,
            ]
          : []),
        {
          key: 'info',
          label: t('common.info'),
          component: InfoColumn,
          maxWidth: 260,
        },
      ],
      sortColumn: {
        column: activeSort.column === 'amount' ? 'amount' : 'date',
        direction: activeSort.direction === 'asc' ? 'ASC' : 'DESC',
      },
      // Two lines of Info text.
      emptyRowHeight: 73,
      noDataAvailable: 'table.no-data',
      pagination: {
        page,
        pageSize,
        pageSizeOptions: [5, 10, 25, 50],
        total: data?.meta.total_count ?? 0,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
      },
    });
  }, [
    navigate,
    groupDetailPath,
    t,
    tab,
    set,
    tabItems,
    filterItems,
    setSort,
    hasAccountingPaymentsNew,
    paymentProcessor,
    activeSort.column,
    activeSort.direction,
    page,
    pageSize,
    data?.meta.total_count,
    setPage,
    setPageSize,
  ]);

  return (
    <>
      <PaginatedTable2
        name="CashListTransactions"
        config={config}
        rows={rows}
        isLoading={isLoading}
        isError={isError}
      />
      {detailsRow && (
        <CoinsModal
          open
          setOpen={(open) => {
            if (!open) {
              setDetailsRow(undefined);
            }
          }}
          coins={detailsRow.coinChange}
          bankAccountAmount={detailsRow.bankAccountAmount}
          amountColor={detailsRow.amountColor}
          amountString={detailsRow.amountString}
          separateFeeAmountColor={detailsRow.separateFeeAmountColor}
          separateFeeAmountString={detailsRow.separateFeeAmountString}
          immediateFeeAmountColor={detailsRow.immediateFeeAmountColor}
          immediateFeeAmountString={detailsRow.immediateFeeAmountString}
          balanceAfterString={detailsRow.balanceAfterString}
          balanceBeforeString={detailsRow.balanceBeforeString}
          selectedCashListName={selectedCashListName}
        />
      )}
    </>
  );
};
